import React from 'react';
import { useTranslation } from 'react-i18next';
import { useFormatters } from '../../../utils/format';
import Icon from '../../../components/AppIcon';

/**
 * DashboardHeader
 * ----------------------------------------------------------------------------
 * Compact summary bar that sits above the metrics grid. Replaces the older
 * FilterPanel, filters can live in a secondary view. For an investor demo,
 * the priorities are: tenant identity, reporting period, compliance posture,
 * and immediate actions.
 */

const complianceBadge = (score, t) => {
  if (score >= 85) return { label: t('dashboard.compliancePosture.compliant'), tone: 'success' };
  if (score >= 70) return { label: t('dashboard.compliancePosture.attention'), tone: 'warning' };
  return { label: t('dashboard.compliancePosture.nonCompliant'), tone: 'destructive' };
};

const toneClasses = {
  success: { dot: 'bg-success', text: 'text-success', bg: 'bg-success/10', ring: 'ring-success/20' },
  warning: { dot: 'bg-warning', text: 'text-warning', bg: 'bg-warning/10', ring: 'ring-warning/20' },
  destructive: { dot: 'bg-destructive', text: 'text-destructive', bg: 'bg-destructive/10', ring: 'ring-destructive/20' },
};

const DashboardHeader = ({ tenant, onRefresh, onExport, onGenerateReport, isLoading }) => {
  const { t } = useTranslation();
  const { num, pct } = useFormatters();
  const badge = complianceBadge(tenant.metrics.complianceScore, t);
  const tone = toneClasses[badge.tone];

  return (
    <div className="mb-6">
      {/* Title row */}
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-4">
        <div>
          <div className="flex items-center gap-2 mb-1.5">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              {t('dashboard.title')}
            </span>
            <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold ${tone.bg} ${tone.text} ring-1 ${tone.ring}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${tone.dot} animate-pulse`} />
              {badge.label}
            </span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight">
            {tenant.name}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {tenant.narrativeTagline}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onRefresh}
            disabled={isLoading}
            className="flex items-center gap-1.5 px-3 py-2 rounded-md border border-border bg-card text-sm font-medium text-foreground hover:bg-muted transition-smooth disabled:opacity-50"
          >
            <Icon name="RefreshCw" size={14} className={isLoading ? 'animate-spin' : ''} />
            <span className="hidden sm:inline">{t('dashboard.actions.refresh')}</span>
          </button>
          <button
            type="button"
            onClick={onExport}
            className="flex items-center gap-1.5 px-3 py-2 rounded-md border border-border bg-card text-sm font-medium text-foreground hover:bg-muted transition-smooth"
          >
            <Icon name="Download" size={14} />
            <span className="hidden sm:inline">{t('dashboard.actions.export')}</span>
          </button>
          <button
            type="button"
            onClick={onGenerateReport}
            className="flex items-center gap-1.5 px-3 py-2 rounded-md bg-gradient-to-r from-teal-600 to-teal-500 text-white text-sm font-semibold shadow-sm hover:shadow-md transition-smooth"
          >
            <Icon name="FileText" size={14} />
            <span>{t('dashboard.actions.directiveReport')}</span>
          </button>
        </div>
      </div>

      {/* Meta row */}
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 pb-4 border-b border-border text-xs">
        <div className="flex items-center gap-1.5">
          <Icon name="Building2" size={12} className="text-muted-foreground" />
          <span className="text-muted-foreground">{t('dashboard.meta.industry')}</span>
          <span className="font-medium text-foreground">{tenant.industry}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Icon name="Users" size={12} className="text-muted-foreground" />
          <span className="text-muted-foreground">{t('dashboard.meta.workers')}</span>
          <span className="font-medium text-foreground tabular-nums">
            {num(tenant.employees)}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <Icon name="Calendar" size={12} className="text-muted-foreground" />
          <span className="text-muted-foreground">{t('dashboard.meta.reportingYear')}</span>
          <span className="font-medium text-foreground">{tenant.reportingYear}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Icon name="UserCheck" size={12} className="text-muted-foreground" />
          <span className="text-muted-foreground">{t('dashboard.meta.femaleShare')}</span>
          <span className="font-medium text-foreground tabular-nums">{pct(tenant.metrics.femaleShare)}</span>
        </div>
        <div className="flex items-center gap-1.5 ml-auto">
          <span className="w-1.5 h-1.5 rounded-full bg-success animate-pulse" />
          <span className="text-muted-foreground">{t('dashboard.meta.hrisSync')}</span>
          <span className="font-medium text-foreground">{t('common.live')}</span>
        </div>
      </div>
    </div>
  );
};

export default DashboardHeader;
